import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  Edit3, 
  Trash2, 
  MapPin, 
  Heart, 
  AlertTriangle,
  Users,
  Camera,
  RefreshCw
} from 'lucide-react';
import Layout from '../../components/Layout';
import { strayyoService } from '../../services';
import { Strayyo } from '../../types';
import StrayyoDetailView from './StrayyoDetailView';
import StrayyoEditView from './StrayyoEditView';

interface StrayyoListResponse {
  strayyos: Strayyo[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

const StrayyosPage: React.FC = () => {
  const [strayyos, setStrayyos] = useState<Strayyo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [healthFilter, setHealthFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Selected strayyo for detail view/edit
  const [selectedStrayyo, setSelectedStrayyo] = useState<Strayyo | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'detail' | 'edit'>('list');

  const fetchStrayyos = async (page = 1, search = '', healthStatus = 'all') => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
      });
      
      if (search.trim()) params.append('search', search.trim());
      if (healthStatus && healthStatus !== 'all') params.append('healthStatus', healthStatus);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/strayyos?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch strayyos');
      }

      const data: StrayyoListResponse = await response.json();
      setStrayyos(data.strayyos);
      setPagination(data.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch strayyos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStrayyos(pagination.page, searchTerm, healthFilter);
  }, [pagination.page, pagination.limit]);

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchStrayyos(1, searchTerm, healthFilter);
  };

  const handleFilterChange = (filter: string) => {
    setHealthFilter(filter);
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchStrayyos(1, searchTerm, filter);
  };

  const handleViewStrayyo = async (strayyo: Strayyo) => {
    try {
      const detailed = await strayyoService.getById(strayyo.id);
      setSelectedStrayyo(detailed);
      setViewMode('detail');
    } catch (err) {
      setError('Failed to fetch strayyo details');
    }
  };

  const handleEditStrayyo = async (strayyo: Strayyo) => {
    try {
      const detailed = await strayyoService.getById(strayyo.id);
      setSelectedStrayyo(detailed);
      setViewMode('edit');
    } catch (err) {
      setError('Failed to fetch strayyo details');
    }
  };

  const handleDeleteStrayyo = async (strayyoId: string) => {
    if (!window.confirm('Are you sure you want to delete this strayyo? This action cannot be undone.')) {
      return;
    }

    try {
      await strayyoService.delete(strayyoId);
      fetchStrayyos(pagination.page, searchTerm, healthFilter);
    } catch (err) {
      setError('Failed to delete strayyo');
    }
  };

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case 'GOOD': return 'text-green-600 bg-green-100';
      case 'FAIR': return 'text-yellow-600 bg-yellow-100';
      case 'POOR': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'DOG': return '🐕';
      case 'CAT': return '🐱';
      default: return '🐾';
    }
  };

  if (viewMode === 'detail' && selectedStrayyo) {
    return (
      <Layout currentPage="strayyos">
        <StrayyoDetailView 
          strayyo={selectedStrayyo} 
          onBack={() => setViewMode('list')}
          onEdit={() => setViewMode('edit')}
        />
      </Layout>
    );
  }

  if (viewMode === 'edit' && selectedStrayyo) {
    return (
      <Layout currentPage="strayyos">
        <StrayyoEditView 
          strayyo={selectedStrayyo} 
          onBack={() => setViewMode('list')}
          onSave={(updated) => {
            setSelectedStrayyo(updated);
            setViewMode('detail');
            fetchStrayyos(pagination.page, searchTerm, healthFilter);
          }}
        />
      </Layout>
    );
  }

  return (
    <Layout currentPage="strayyos">
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold text-gray-900">Strayyos Management</h1>
            <p className="mt-2 text-sm text-gray-700">
              Manage all strayyos in the system, their health status, location, and associated angels.
            </p>
          </div>
          <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
            <button
              type="button"
              onClick={() => fetchStrayyos(pagination.page, searchTerm, healthFilter)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mt-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, UID, or area..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
            </div>
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              Search
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="bg-gray-50 p-4 rounded-lg space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Health Status</label>
                <div className="flex flex-wrap gap-2">
                  {['all', 'GOOD', 'FAIR', 'POOR', 'UNKNOWN'].map((status) => (
                    <button
                      key={status}
                      onClick={() => handleFilterChange(status)}
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        healthFilter === status
                          ? 'bg-orange-600 text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {status === 'all' ? 'All' : status.charAt(0) + status.slice(1).toLowerCase()}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-4">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Heart className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Strayyos</dt>
                    <dd className="text-lg font-medium text-gray-900">{pagination.total}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">With Angels</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {strayyos.filter(s => s.angels.length > 0).length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-6 w-6 text-red-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Active Emergencies</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {strayyos.reduce((count, s) => count + (s._count?.emergencies || 0), 0)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Camera className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">With Photos</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {strayyos.filter(s => (s._count?.images || s.images.length) > 0).length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Strayyos Table */}
        <div className="mt-8 flex flex-col">
          <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Strayyo
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Health
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Location
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Angels
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Photos
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Followers
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Status
                      </th>
                      <th className="relative px-4 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-12 text-center">
                          <div className="flex items-center justify-center">
                            <RefreshCw className="h-6 w-6 animate-spin text-gray-400 mr-2" />
                            <span className="text-gray-500">Loading strayyos...</span>
                          </div>
                        </td>
                      </tr>
                    ) : strayyos.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                          No strayyos found. {searchTerm && 'Try adjusting your search criteria.'}
                        </td>
                      </tr>
                    ) : (
                      strayyos.map((strayyo) => (
                        <tr key={strayyo.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-10 w-10 flex-shrink-0">
                                {strayyo.images[0]?.url ? (
                                  <img
                                    className="h-10 w-10 rounded-full object-cover"
                                    src={strayyo.images[0].url}
                                    alt={strayyo.name || strayyo.uid}
                                  />
                                ) : (
                                  <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                    <span className="text-lg">{getTypeIcon(strayyo.type)}</span>
                                  </div>
                                )}
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {strayyo.name || 'Unnamed'}
                                </div>
                                <div className="text-sm text-gray-500">UID: {strayyo.uid}</div>
                                <div className="text-xs text-gray-400">{strayyo.type}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getHealthStatusColor(strayyo.healthStatus)}`}>
                              {strayyo.healthStatus.toLowerCase()}
                            </span>
                            <div className="mt-1 text-xs text-gray-500">
                              {strayyo.vaccinated && <span className="mr-2">💉 Vaccinated</span>}
                              {strayyo.sterilized && <span>✂️ Sterilized</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-900">
                              <MapPin className="h-4 w-4 text-gray-400 mr-1" />
                              <span>{strayyo.area || 'Unknown'}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm">
                              {strayyo.angels.length > 0 ? (
                                <div>
                                  <div className="text-gray-900 font-medium">
                                    {strayyo.angels
                                      .map(a => a.angel?.user?.name || a.angel?.user?.email || 'Unknown')
                                      .join(', ')}
                                  </div>
                                  <div className="text-xs text-gray-400 mt-0.5">
                                    {strayyo.angels.filter(a => a.role === 'PRIMARY').length}P / {strayyo.angels.filter(a => a.role !== 'PRIMARY').length}S
                                  </div>
                                </div>
                              ) : (
                                <span className="text-gray-400 text-xs">None</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center text-sm">
                              <Camera className="h-4 w-4 text-gray-400 mr-1" />
                              <span className={(strayyo._count?.images || strayyo.images.length) > 0 ? 'text-gray-900' : 'text-gray-400'}>
                                {strayyo._count?.images ?? strayyo.images.length}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm text-gray-600">
                              {strayyo._count?.followers ?? 0}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex flex-col space-y-1">
                              {(strayyo._count?.emergencies || 0) > 0 && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  <AlertTriangle className="h-3 w-3 mr-1" />
                                  Emergency
                                </span>
                              )}
                              {(strayyo._count?.publicReports || 0) > 0 && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  {strayyo._count?.publicReports} Reports
                                </span>
                              )}
                              {strayyo.lastFed && (
                                <span className="text-xs text-gray-500">
                                  Fed {new Date(strayyo.lastFed).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleViewStrayyo(strayyo)}
                                className="text-orange-600 hover:text-orange-900"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleEditStrayyo(strayyo)}
                                className="text-blue-600 hover:text-blue-900"
                                title="Edit"
                              >
                                <Edit3 className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteStrayyo(strayyo.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
                disabled={pagination.page === pagination.pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span>
                  {' '}to{' '}
                  <span className="font-medium">
                    {Math.min(pagination.page * pagination.limit, pagination.total)}
                  </span>
                  {' '}of{' '}
                  <span className="font-medium">{pagination.total}</span>
                  {' '}results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={pagination.page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    const page = Math.max(1, pagination.page - 2) + i;
                    if (page > pagination.pages) return null;
                    return (
                      <button
                        key={page}
                        onClick={() => setPagination(prev => ({ ...prev, page }))}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === pagination.page
                            ? 'z-10 bg-orange-50 border-orange-500 text-orange-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
                    disabled={pagination.page === pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StrayyosPage;